import type { LegalDoc } from './types';

export const deleteAccountDoc: LegalDoc = {
  slug: 'delete-account',
  title: 'Delete Your Account',
  effectiveDate: '2026-06-15',
  lastUpdated: '2026-06-15',
  intro:
    `You are in control of your Sansara account and your data. This page explains how to permanently delete your Sansara account and personal data, what gets deleted, what we may need to retain by law, and how long it takes. You can start the process inside the app or here on the web — no phone call required.`,
  sections: [
    {
      heading: 'Delete From Within the App',
      paragraphs: [
        `The quickest way to delete your account is from the app itself:`,
      ],
      bullets: [
        `Open the Sansara app and sign in.`,
        `Go to Settings > Account > Delete Account.`,
        `Read the confirmation screen, which explains what will be deleted.`,
        `Confirm by verifying the one-time password (OTP) sent to your registered email or mobile.`,
        `Your deletion request is submitted immediately, and your account is deactivated right away.`,
      ],
    },
    {
      heading: 'Delete From the Web',
      paragraphs: [
        `If you cannot access the app, you can request deletion on the web at https://sansarafin.com/delete-account. Submit the request using your registered email address; we will verify your identity (for example, by OTP) before processing, to protect your account from unauthorised deletion. This web path satisfies the requirement to offer account deletion without needing to reinstall or open the app.`,
      ],
    },
    {
      heading: 'What Gets Deleted',
      paragraphs: [
        `When your deletion request is processed, we erase or irreversibly anonymise the personal data associated with your account, including:`,
      ],
      bullets: [
        `your profile details (name, email, mobile number);`,
        `your manually entered portfolio, holdings, transactions and notes;`,
        `your watchlists, alerts and preferences;`,
        `any Zerodha connection tokens, so the link to your broker account is revoked on our side;`,
        `your authentication and session data.`,
      ],
    },
    {
      heading: 'What We May Retain (and Why)',
      paragraphs: [
        `Certain records must be kept for a limited period to meet legal, tax, accounting and fraud-prevention obligations — for example, invoices and transaction records relating to subscription payments processed through Razorpay. We retain these only for the minimum period required by applicable law, keep them secured and access-restricted, and delete them once the obligation ends. We do not use retained records for any other purpose.`,
      ],
    },
    {
      heading: 'How Long It Takes',
      paragraphs: [
        `Your account is deactivated immediately on confirmation, so you can no longer sign in. Personal data is then erased or anonymised within a reasonable period, typically within thirty (30) days, except for the limited records described above. We will confirm completion by email.`,
      ],
    },
    {
      heading: 'Before You Delete',
      paragraphs: [
        `Deletion is permanent and cannot be undone. Before you proceed, you may wish to:`,
      ],
      bullets: [
        `cancel any active subscription (deletion does not by itself trigger a refund; see our Refund and Cancellation Policy);`,
        `export or note down any portfolio data you want to keep, as it cannot be recovered after deletion;`,
        `unlink your Zerodha account if you prefer to do so first (deletion will revoke the link on our side in any case).`,
      ],
    },
    {
      heading: 'Subscriptions Purchased Through Apple',
      paragraphs: [
        `If you subscribed through the Apple App Store, deleting your Sansara account does not automatically cancel that subscription. Please also cancel it in your Apple ID subscription settings to stop future charges, as Apple manages those billing arrangements.`,
      ],
    },
    {
      heading: 'Need Help?',
      paragraphs: [
        `If you have trouble deleting your account, or want to exercise any other data right under the Digital Personal Data Protection Act, 2023, contact our Grievance Officer at grievance@sansarafin.com or our support team at support@sansarafin.com. We are happy to help.`,
      ],
    },
  ],
};
